import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/layout/Layout';
import StripeProvider from './components/payment/StripeProvider';

// Auth Pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail';

// Client Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';
import Documents from './pages/Documents';
import Support from './pages/Support';
import WorkOrderNumberMigration from './pages/admin/WorkOrderNumberMigration';
import BookService from './pages/BookService';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import Settings from './pages/Settings';

// Admin Pages
import AdminDashboard from './components/admin/AdminDashboard';

// Contractor Pages
import ContractorDashboard from './pages/contractor/ContractorDashboard';
import AvailableJobs from './pages/contractor/AvailableJobs';
import MyAssignedJobs from './pages/contractor/MyAssignedJobs';
import ContractorJobDetail from './pages/contractor/ContractorJobDetail';
import ContractorProfile from './pages/contractor/ContractorProfile';
import ContractorPaymentsView from './pages/contractor/ContractorPaymentsView'; // ✅ NEW

// ENHANCED TRAFFIC COP: Directs traffic based on role
function DashboardRoute() {
    const { userProfile } = useAuth();

    // Admin → Admin Dashboard
    if (userProfile?.role === 'admin') {
        return <Navigate to="/admin" replace />;
    }

    // Contractor → Contractor Dashboard
    if (userProfile?.role === 'contractor') {
        return <Navigate to="/contractor/dashboard" replace />;
    }

    // Default: Client → Client Dashboard
    return (
        <Layout>
            <Dashboard />
        </Layout>
    );
}

function App() {
    return (
        <Router>
            <AuthProvider>
                <StripeProvider>
                    <Routes>
                        {/* ============== PUBLIC ROUTES ============== */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />

                        {/* ============== DASHBOARD ROUTER (Traffic Cop) ============== */}
                        <Route
                            path="/dashboard"
                            element={
                                <PrivateRoute>
                                    <DashboardRoute />
                                </PrivateRoute>
                            }
                        />

                        {/* ============== CLIENT ROUTES ============== */}
                        <Route
                            path="/book-service"
                            element={
                                <PrivateRoute>
                                    <Layout>
                                        <BookService />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />


                        {/* Client workspace: documents and support */}
                        <Route path="/documents" element={<PrivateRoute><Layout><Documents /></Layout></PrivateRoute>} />
                        <Route path="/support" element={<PrivateRoute><Layout><Support /></Layout></PrivateRoute>} />
                        {/* One-time admin-only ID migration */}
                        <Route path="/admin/order-numbers" element={<PrivateRoute requireAdmin={true}><Layout><WorkOrderNumberMigration /></Layout></PrivateRoute>} />

                        <Route
                            path="/jobs"
                            element={
                                <PrivateRoute>
                                    <Layout>
                                        <Jobs />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/jobs/:jobId"
                            element={
                                <PrivateRoute>
                                    <Layout>
                                        <JobDetail />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        {/* ============== ADMIN ROUTES ============== */}
                        <Route
                            path="/admin/*"
                            element={
                                <PrivateRoute requireAdmin={true}>
                                    <Layout>
                                        <AdminDashboard />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        {/* ============== CONTRACTOR ROUTES ============== */}
                        <Route
                            path="/contractor/dashboard"
                            element={
                                <PrivateRoute requireContractor={true}>
                                    <Layout>
                                        <ContractorDashboard />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/contractor/available-jobs"
                            element={
                                <PrivateRoute requireContractor={true}>
                                    <Layout>
                                        <AvailableJobs />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/contractor/my-jobs"
                            element={
                                <PrivateRoute requireContractor={true}>
                                    <Layout>
                                        <MyAssignedJobs />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/contractor/jobs/:jobId"
                            element={
                                <PrivateRoute requireContractor={true}>
                                    <Layout>
                                        <ContractorJobDetail />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/contractor/profile"
                            element={
                                <PrivateRoute requireContractor={true}>
                                    <Layout>
                                        <ContractorProfile />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        {/* ✅ NEW: Contractor Payments Route */}
                        <Route
                            path="/contractor/payments"
                            element={
                                <PrivateRoute requireContractor={true}>
                                    <Layout>
                                        <ContractorPaymentsView />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        {/* ============== SHARED ROUTES ============== */}
                        <Route
                            path="/settings"
                            element={
                                <PrivateRoute>
                                    <Layout>
                                        <Settings />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />

                        {/* ============== DEFAULT ROUTES ============== */}
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </StripeProvider>
            </AuthProvider>
        </Router>
    );
}

export default App;
